import React, { useEffect, useMemo, useState } from 'react';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../auth/authContext';
import { updateProfilePassword, updateProfilePhone, updateProfileName, updateRoleSwitchPin } from '../../services/authApi';
import './ProfileModal.css';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const PHONE_PREFIX = '+998';

function digitsOnly(v: string) {
  return v.replace(/\D/g, '');
}

function formatPhoneWithSpaces(digits: string) {
  if (!digits.length) return '';
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
  if (digits.length <= 7) return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
  return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 7)} ${digits.slice(7, 9)}`;
}

const PASSWORD_MIN_LENGTH = 6;

function getErrorMessage(err: unknown) {
  if (err && typeof err === 'object') {
    const e = err as Record<string, unknown>;
    if (e.message != null && String(e.message).trim()) return String(e.message);
    const data = e.data;
    if (data && typeof data === 'object' && 'message' in (data as Record<string, unknown>)) {
      const msg = (data as Record<string, unknown>).message;
      if (msg != null && String(msg).trim()) return String(msg);
    }
  }
  return String(err ?? '');
}

function getErrorCode(err: unknown) {
  if (!err || typeof err !== 'object') return '';
  const e = err as Record<string, unknown>;
  const data = e.data;
  if (data && typeof data === 'object' && 'code' in (data as Record<string, unknown>)) {
    return String((data as Record<string, unknown>).code ?? '');
  }
  if (e.code != null) return String(e.code);
  return '';
}

export function ProfileModal({ isOpen, onClose }: Props) {
  useBodyScrollLock(isOpen);
  const { t } = useLanguage();
  const { user, baseUser, setUser } = useAuth();
  const [phone, setPhone] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [pinEditing, setPinEditing] = useState(false);
  const [pinCurrent, setPinCurrent] = useState('');
  const [pinNew, setPinNew] = useState('');
  const [pinNewConfirm, setPinNewConfirm] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinSaved, setPinSaved] = useState(false);
  const [passwordEditing, setPasswordEditing] = useState(false);
  const [passwordCurrent, setPasswordCurrent] = useState('');
  const [passwordNew, setPasswordNew] = useState('');
  const [passwordNewConfirm, setPasswordNewConfirm] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaved, setPasswordSaved] = useState(false);
  const showPinBlock = (baseUser ?? user)?.role === 'tenant_owner' || (baseUser ?? user)?.role === 'cashier';
  const showNameField =
    (baseUser ?? user)?.role === 'regional_manager' ||
    (baseUser ?? user)?.role === 'dispatcher';
  const hasPin = !!(baseUser ?? user)?.roleSwitchPinSet;
  const hasLoginPassword = !!(baseUser ?? user)?.loginPasswordSet;

  const initialPhoneDigits = useMemo(() => {
    const fromPhone = user?.phone ? digitsOnly(user.phone) : '';
    if (fromPhone.length >= 9) return fromPhone.slice(-9);

    const fromName = user?.name ? digitsOnly(user.name) : '';
    return fromName.length >= 9 ? fromName.slice(-9) : '';
  }, [user?.phone, user?.name]);

  const initialDisplayName = useMemo(() => {
    const n = user?.name != null ? String(user.name).trim() : '';
    if (!n) return '';
    const digits = digitsOnly(n);
    if (digits.length >= 9 && digits === n.replace(/\D/g, '')) return '';
    return n;
  }, [user?.name]);

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setSaved(false);
    setEditing(false);
    setPhone(initialPhoneDigits);
    setDisplayName(initialDisplayName);
    setPinEditing(false);
    setPinCurrent('');
    setPinNew('');
    setPinNewConfirm('');
    setPinError('');
    setPinSaved(false);
    setPasswordEditing(false);
    setPasswordCurrent('');
    setPasswordNew('');
    setPasswordNewConfirm('');
    setPasswordError('');
    setPasswordSaved(false);
  }, [isOpen, initialPhoneDigits, initialDisplayName]);

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const onOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const onPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = digitsOnly(e.target.value).slice(0, 9);
    setPhone(v);
    setError('');
    setSaved(false);
  };

  const fullPhone = `${PHONE_PREFIX}${phone}`;

  const onPinSave = async () => {
    setPinError('');
    const newP = pinNew.trim();
    const conf = pinNewConfirm.trim();
    if (newP.length < 4) {
      setPinError(t('roleSwitch_pin_min') || 'PIN kamida 4 belgi');
      return;
    }
    if (newP !== conf) {
      setPinError(t('roleSwitch_pin_mismatch') || 'PIN mos emas');
      return;
    }
    try {
      const res = await updateRoleSwitchPin({
        currentPin: hasPin ? pinCurrent.trim() : undefined,
        newPin: newP,
        newPinConfirm: conf,
      });
      if (res && typeof res === 'object' && 'user' in (res as Record<string, unknown>)) {
        const updated = (res as any).user;
        const prev = baseUser ?? user;
        if (prev) {
          setUser({
            ...prev,
            roleSwitchPinSet:
              (updated as any).role_switch_pin_set ??
              (updated as any).roleSwitchPinSet ??
              true,
          });
        }
      }
      setPinCurrent('');
      setPinNew('');
      setPinNewConfirm('');
      setPinEditing(false);
      setPinSaved(true);
    } catch (e: any) {
      const code = getErrorCode(e);
      if (code === 'current_pin_invalid') {
        setPinError(t('roleSwitch_pin_invalid') || 'Joriy PIN noto\'g\'ri');
      } else {
        setPinError(getErrorMessage(e));
      }
    }
  };

  const onPasswordSave = async () => {
    setPasswordError('');
    setPasswordSaved(false);
    const current = passwordCurrent.trim();
    const next = passwordNew.trim();
    const confirm = passwordNewConfirm.trim();
    if (hasLoginPassword && !current) {
      setPasswordError(t('adminProfile_password_current_required') || 'Joriy parolni kiriting');
      return;
    }
    if (next.length < PASSWORD_MIN_LENGTH) {
      setPasswordError(t('adminProfile_password_min') || `Yangi parol kamida ${PASSWORD_MIN_LENGTH} belgi bo'lishi kerak`);
      return;
    }
    if (next !== confirm) {
      setPasswordError(t('adminProfile_password_mismatch') || 'Yangi parol tasdiqlash bilan mos emas');
      return;
    }
    setPasswordSaving(true);
    try {
      const res = await updateProfilePassword({
        currentPassword: hasLoginPassword ? current : undefined,
        newPassword: next,
        newPasswordConfirm: confirm,
      });
      if (res && typeof res === 'object' && 'user' in (res as Record<string, unknown>)) {
        const updated = (res as Record<string, unknown>).user;
        const prev = baseUser ?? user;
        if (prev && updated && typeof updated === 'object') {
          setUser({
            ...prev,
            loginPasswordSet:
              (updated as any).login_password_set ??
              (updated as any).loginPasswordSet ??
              true,
          });
        }
      } else {
        const prev = baseUser ?? user;
        if (prev) setUser({ ...prev, loginPasswordSet: true });
      }
      setPasswordCurrent('');
      setPasswordNew('');
      setPasswordNewConfirm('');
      setPasswordEditing(false);
      setPasswordSaved(true);
    } catch (e) {
      const code = getErrorCode(e);
      if (code === 'current_password_invalid') {
        setPasswordError(t('adminProfile_password_invalid') || 'Joriy parol noto\'g\'ri');
      } else {
        setPasswordError(getErrorMessage(e));
      }
    } finally {
      setPasswordSaving(false);
    }
  };

  const onSave = async () => {
    if (phone.length !== 9) {
      setError(t('loginModalPhoneError') || 'Telefon raqam noto‘g‘ri');
      return;
    }
    if (showNameField && !displayName.trim()) {
      setError(t('adminProfile_name_required') || 'Ism kiriting');
      return;
    }

    const phoneChanged = phone !== initialPhoneDigits;
    const nameChanged = showNameField && displayName.trim() !== initialDisplayName;
    if (!phoneChanged && !nameChanged) {
      setSaved(true);
      setEditing(false);
      return;
    }

    setLoading(true);
    setError('');
    setSaved(false);
    try {
      let maybeUser: unknown = null;
      if (phoneChanged) {
        const res = await updateProfilePhone(fullPhone);
        if (res && typeof res === 'object' && 'user' in (res as Record<string, unknown>)) {
          maybeUser = (res as Record<string, unknown>).user;
        }
      }
      if (showNameField && nameChanged) {
        const nameRes = await updateProfileName(displayName.trim());
        if (nameRes && typeof nameRes === 'object' && 'user' in (nameRes as Record<string, unknown>)) {
          maybeUser = (nameRes as Record<string, unknown>).user;
        }
      }
      const u = baseUser ?? user;
      if (maybeUser && typeof maybeUser === 'object') {
        const nextName = (maybeUser as Record<string, unknown>).name ?? (maybeUser as Record<string, unknown>).phone;
        const nextPhone = (maybeUser as Record<string, unknown>).phone;
        setUser(u ? {
          ...u,
          name: nextName != null ? String(nextName) : u.name,
          phone: nextPhone != null ? String(nextPhone) : (phoneChanged ? fullPhone : u.phone),
        } : u);
      } else if (u) {
        setUser({
          ...u,
          phone: phoneChanged ? fullPhone : u.phone,
          name: showNameField && nameChanged ? displayName.trim() : u.name,
        });
      }
      setSaved(true);
      setEditing(false);
    } catch (e) {
      const msg = getErrorMessage(e);
      if (msg === 'PHONE_ALREADY_EXISTS') {
        setError(t('adminProfile_phone_already_exists') || 'Bu telefon raqami boshqa foydalanuvchida ro\'yxatdan o\'tgan');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="profile-modal-overlay" onClick={onOverlayClick} role="dialog" aria-modal="true" aria-labelledby="profile-modal-title">
      <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="profile-modal-close" onClick={onClose} aria-label={t('loginModalClose') || 'Close'}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <h2 id="profile-modal-title" className="profile-modal-title">{t('adminProfile') || 'Profil'}</h2>
        <p className="profile-modal-subtitle">{t('adminProfileSettings') || 'Profil sozlamalari'}</p>

        <div className="profile-modal-card">
          <div className="profile-modal-row">
            <span className="profile-modal-label">{t('adminCurrentRole') || 'Role'}:</span>
            <span className="profile-modal-value">{user?.role ? (t(`roleTitle_${user.role}`) || user.role) : '-'}</span>
          </div>
        </div>

        {showNameField ? (
          <div className="profile-modal-field">
            <label className="profile-modal-label" htmlFor="profile-name">
              {t('adminProfile_name_label') || 'Ism'}
            </label>
            <input
              id="profile-name"
              type="text"
              className="profile-modal-input"
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                setError('');
                setSaved(false);
              }}
              disabled={!editing || loading}
              placeholder={t('adminProfile_name_placeholder') || 'Ism va familiya'}
              autoComplete="name"
            />
          </div>
        ) : null}

        <div className="profile-modal-field">
          <label className="profile-modal-label" htmlFor="profile-phone">{t('loginModalPhoneLabel') || 'Telefon'} </label>
          <div className={`profile-modal-phone-wrap ${editing ? 'editing' : ''}`}>
            <span className="profile-modal-prefix">{PHONE_PREFIX}</span>
            <input
              id="profile-phone"
              type="tel"
              className="profile-modal-input"
              value={formatPhoneWithSpaces(phone)}
              onChange={onPhoneChange}
              disabled={!editing || loading}
              placeholder={t('loginModalPhonePlaceholder') || ''}
              autoComplete="tel"
            />
          </div>
        </div>

        {!!error && <p className="profile-modal-error">{error}</p>}
        {saved && <p className="profile-modal-success">{t('saved') || 'Saqlandi'}</p>}

        {showPinBlock && (
          <div className="profile-modal-card profile-modal-pin-block">
            <div className="profile-modal-row">
              <span className="profile-modal-label">{t('roleSwitch_pin_profile_label') || 'Rol almashtirish PIN'}</span>
              <span className="profile-modal-value">{hasPin ? '••••••' : '—'}</span>
            </div>
            {!pinEditing ? (
              <button type="button" className="profile-modal-btn profile-modal-btn-secondary profile-modal-pin-edit" onClick={() => { setPinEditing(true); setPinError(''); setPinSaved(false); }}>
                {t('roleSwitch_pin_change') || 'O\'zgartirish'}
              </button>
            ) : (
              <div className="profile-modal-pin-fields">
                {hasPin && (
                  <>
                    <label className="profile-modal-label" htmlFor="profile-pin-current">{t('roleSwitch_pin_current') || 'Joriy PIN'}</label>
                    <input id="profile-pin-current" type="password" inputMode="numeric" autoComplete="off" className="profile-modal-input" value={pinCurrent} onChange={(e) => { setPinCurrent(e.target.value.replace(/\D/g, '').slice(0, 8)); setPinError(''); }} placeholder="••••" maxLength={8} />
                  </>
                )}
                <label className="profile-modal-label" htmlFor="profile-pin-new">{hasPin ? (t('roleSwitch_pin_new') || 'Yangi PIN') : (t('roleSwitch_set_pin_first') || 'PIN o\'rnating')}</label>
                <input id="profile-pin-new" type="password" inputMode="numeric" autoComplete="new-password" className="profile-modal-input" value={pinNew} onChange={(e) => { setPinNew(e.target.value.replace(/\D/g, '').slice(0, 8)); setPinError(''); }} placeholder="••••" maxLength={8} />
                <label className="profile-modal-label" htmlFor="profile-pin-confirm">{t('roleSwitch_pin_confirm') || 'PIN takrorlang'}</label>
                <input id="profile-pin-confirm" type="password" inputMode="numeric" autoComplete="new-password" className="profile-modal-input" value={pinNewConfirm} onChange={(e) => { setPinNewConfirm(e.target.value.replace(/\D/g, '').slice(0, 8)); setPinError(''); }} placeholder="••••" maxLength={8} />
                {pinError && <p className="profile-modal-error">{pinError}</p>}
                {pinSaved && <p className="profile-modal-success">{t('saved') || 'Saqlandi'}</p>}
                <div className="profile-modal-pin-actions">
                  <button type="button" className="profile-modal-btn profile-modal-btn-secondary" onClick={() => { setPinEditing(false); setPinCurrent(''); setPinNew(''); setPinNewConfirm(''); setPinError(''); }}>
                    {t('adminBranch_cancel') || 'Bekor qilish'}
                  </button>
                  <button type="button" className="profile-modal-btn profile-modal-btn-primary" onClick={onPinSave}>
                    {t('save') || 'Saqlash'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="profile-modal-card profile-modal-pin-block">
          <div className="profile-modal-row">
            <span className="profile-modal-label">{t('adminSettings_login_password') || 'Kirish paroli'}</span>
            <span className="profile-modal-value">{hasLoginPassword ? '••••••••' : '—'}</span>
          </div>
          {!passwordEditing ? (
            <button
              type="button"
              className="profile-modal-btn profile-modal-btn-secondary profile-modal-pin-edit"
              onClick={() => {
                setPasswordEditing(true);
                setPasswordError('');
                setPasswordSaved(false);
              }}
            >
              {t('roleSwitch_pin_change') || 'O\'zgartirish'}
            </button>
          ) : (
            <div className="profile-modal-pin-fields">
              {hasLoginPassword && (
                <>
                  <label className="profile-modal-label" htmlFor="profile-password-current">
                    {t('adminProfile_password_current') || 'Joriy parol'}
                  </label>
                  <input
                    id="profile-password-current"
                    type="password"
                    autoComplete="current-password"
                    className="profile-modal-input"
                    value={passwordCurrent}
                    onChange={(e) => { setPasswordCurrent(e.target.value); setPasswordError(''); }}
                    placeholder="••••••••"
                    disabled={passwordSaving}
                  />
                </>
              )}
              <label className="profile-modal-label" htmlFor="profile-password-new">
                {hasLoginPassword
                  ? (t('adminProfile_password_new') || 'Yangi parol')
                  : (t('adminProfile_password_set_first') || 'Kirish parolini o\'rnating')}
              </label>
              <input
                id="profile-password-new"
                type="password"
                autoComplete="new-password"
                className="profile-modal-input"
                value={passwordNew}
                onChange={(e) => { setPasswordNew(e.target.value); setPasswordError(''); }}
                placeholder="••••••••"
                disabled={passwordSaving}
              />
              <label className="profile-modal-label" htmlFor="profile-password-confirm">
                {t('adminProfile_password_confirm') || 'Yangi parolni tasdiqlang'}
              </label>
              <input
                id="profile-password-confirm"
                type="password"
                autoComplete="new-password"
                className="profile-modal-input"
                value={passwordNewConfirm}
                onChange={(e) => { setPasswordNewConfirm(e.target.value); setPasswordError(''); }}
                placeholder="••••••••"
                disabled={passwordSaving}
              />
              {passwordError && <p className="profile-modal-error">{passwordError}</p>}
              {passwordSaved && <p className="profile-modal-success">{t('saved') || 'Saqlandi'}</p>}
              <div className="profile-modal-pin-actions">
                <button
                  type="button"
                  className="profile-modal-btn profile-modal-btn-secondary"
                  disabled={passwordSaving}
                  onClick={() => {
                    setPasswordEditing(false);
                    setPasswordCurrent('');
                    setPasswordNew('');
                    setPasswordNewConfirm('');
                    setPasswordError('');
                    setPasswordSaved(false);
                  }}
                >
                  {t('adminBranch_cancel') || 'Bekor qilish'}
                </button>
                <button
                  type="button"
                  className="profile-modal-btn profile-modal-btn-primary"
                  disabled={passwordSaving}
                  onClick={onPasswordSave}
                >
                  {passwordSaving ? (t('saving') || 'Saqlanmoqda...') : (t('save') || 'Saqlash')}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="profile-modal-actions">
          {!editing ? (
            <button type="button" className="profile-modal-btn profile-modal-btn-secondary" onClick={() => { setEditing(true); setSaved(false); }}>
              {t('edit') || 'Tahrirlash'}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="profile-modal-btn profile-modal-btn-secondary"
                onClick={() => { setEditing(false); setPhone(initialPhoneDigits); setDisplayName(initialDisplayName); setError(''); setSaved(false); }}
                disabled={loading}
              >
                {t('cancel') || 'Bekor qilish'}
              </button>
              <button
                type="button"
                className="profile-modal-btn profile-modal-btn-primary"
                onClick={onSave}
                disabled={loading}
              >
                {loading ? (t('saving') || 'Saqlanmoqda...') : (t('save') || 'Saqlash')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
